import { sseLogoutListener } from "@/core";
import { useWidgetStore } from "@/state";
import type { RedirectTarget } from "@/types";
import { resolveWidgetReady as resolveBootstrapReady } from "@/bootstrap";
import { clearAlreadyLoggedInCache } from "./alreadyLoggedInCache";

const getTopWindowSafe = (): Window => {
  try {
    return window.top ?? window;
  } catch {
    // cross-origin access can throw
    return window;
  }
};

export const redirectTo = (url: string, target: RedirectTarget) => {
  const w = target === "top" ? getTopWindowSafe() : window;

  try {
    w.location.assign(url);
  } catch (e) {
    try {
      w.location.href = url;
    } catch {
      console.error("[telekomId] redirect failed:", e, url);
    }
  }
};

export const readAttr = (el: Element, name: string) =>
  el.getAttribute(name) ?? undefined;

export const clearLocalSession = () => {
  sseLogoutListener.close();
  useWidgetStore.getState().setState({ user: null, loggedIn: false });
  useWidgetStore.getState().setMenuAnchorEl(null);
  useWidgetStore.getState().setTriggerEl(null);

  clearAlreadyLoggedInCache();

  try {
    sessionStorage.removeItem("cosIdInstallationId");
  } catch {}
};

export const applyOptAttr = <T extends Record<string, any>>(
  target: T,
  key: keyof T,
  el: HTMLElement,
  attrName: string,
  opts?: { trim?: boolean },
) => {
  let v = el.getAttribute(attrName);
  if (v == null) return;

  if (opts?.trim !== false) v = v.trim();
  if (v === "") return;

  (target as any)[key] = v;
};

export const resolveWidgetReady = resolveBootstrapReady;

export const toStringOrNull = (data: unknown): string | null => {
  if (typeof data === "string") return data;
  if (data == null) return null;

  try {
    return String(data);
  } catch {
    return null;
  }
};

export const notifyHostLogout = () => {
  window.postMessage("##telekomId-sso-logout##", window.location.origin);
};

export const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const AVATAR_WIDTH_MIN = 8;
const AVATAR_WIDTH_MAX = 512;

export const parseOptionalAvatarWidthPx = (
  v: string | number | undefined,
): number | undefined => {
  if (v === undefined) return undefined;
  const str = typeof v === "number" ? String(v) : v.trim();
  if (str === "") return undefined;
  const n = Number(str);
  if (!Number.isFinite(n)) return undefined;
  const px = Math.round(n);
  if (px < AVATAR_WIDTH_MIN || px > AVATAR_WIDTH_MAX) return undefined;
  return px;
};

export const normalizeId = (id?: string) => {
  const v = (id ?? "").trim();
  if (!v) return null;
  return v.startsWith("#") ? v.slice(1) : v;
};

export const injectHtmlAndExecuteScripts = (
  holder: HTMLElement,
  html: string,
) => {
  if (holder.dataset.injected === "true") return;

  holder.innerHTML = "";

  const tpl = document.createElement("template");
  tpl.innerHTML = html;

  holder.appendChild(tpl.content.cloneNode(true));

  const scripts = holder.querySelectorAll("script");
  scripts.forEach((oldScript) => {
    const s = document.createElement("script");

    for (const { name, value } of Array.from(oldScript.attributes)) {
      s.setAttribute(name, value);
    }

    if (!oldScript.src) s.text = oldScript.textContent ?? "";

    oldScript.replaceWith(s);
  });

  holder.dataset.injected = "true";
};

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

export const isFullViewportAnchor = (el: HTMLElement): boolean =>
  el === document.body || el === document.documentElement;

export const computeAnchoredPopoverPosition = (
  anchor: HTMLElement,
  popoverWidth: number,
  popoverHeight: number,
): { top: number; left: number } => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (isFullViewportAnchor(anchor)) {
    return {
      top: Math.max(VIEWPORT_MARGIN, (vh - popoverHeight) / 2),
      left: Math.max(VIEWPORT_MARGIN, (vw - popoverWidth) / 2),
    };
  }

  const a = anchor.getBoundingClientRect();
  let left = (a.left + a.right) / 2 - popoverWidth / 2;
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, vw - popoverWidth - VIEWPORT_MARGIN),
  );

  let top = a.bottom + ANCHOR_GAP;
  if (top + popoverHeight > vh - VIEWPORT_MARGIN) {
    const aboveTop = a.top - ANCHOR_GAP - popoverHeight;
    if (aboveTop >= VIEWPORT_MARGIN) {
      top = aboveTop;
    } else {
      top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(top, vh - popoverHeight - VIEWPORT_MARGIN),
      );
    }
  }
  return { top, left };
};
